import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { type ColumnDef } from '@tanstack/react-table';
import { Files } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Appointment } from 'types/appointments';
import { type Report } from 'types/files';
import { type PatientRecord } from 'types/patient';
import {
  formatRecordDate,
  getRecordContractCode,
  getRecordEndDate,
  getRecordStatusClassName,
  getRecordStatusLabel,
  getSickLeaveStartDate,
} from '../../utils/records';
import { ExpandableRecordsTable } from '../ExpandableRecordsTable';
import { AssociatedSessions } from './AssociatedSessions';

interface RecordsHistoryProps {
  records: PatientRecord[];
  appointments: Appointment[];
  reports: Report[];
  isLoading: boolean;
}

export const RecordsHistory = ({
  records,
  appointments,
  reports,
  isLoading,
}: RecordsHistoryProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  const columns = useMemo<ColumnDef<PatientRecord>[]>(
    () => [
      {
        id: 'code',
        accessorKey: 'code',
        enableSorting: true,
        header: (): string => t('PatientRecords.fields.code'),
        cell: ({ row }): string => row.original.code,
      },
      {
        id: 'start',
        enableSorting: false,
        header: (): string => t('PatientRecords.fields.start'),
        cell: ({ row }): string => formatRecordDate(getSickLeaveStartDate(row.original)),
      },
      {
        id: 'end',
        enableSorting: false,
        header: (): string => t('PatientRecords.fields.end'),
        cell: ({ row }): string => getRecordEndDate(row.original),
      },
      {
        id: 'contract',
        enableSorting: false,
        header: (): string => t('PatientRecords.fields.contractShort'),
        cell: ({ row }): string => getRecordContractCode(row.original),
      },
      {
        id: 'status',
        enableSorting: false,
        header: (): string => t('PatientRecords.fields.status'),
        cell: ({ row }): React.ReactElement => (
          <span
            className={cn('text-sm font-medium', getRecordStatusClassName(row.original.status))}
          >
            {getRecordStatusLabel(row.original.status)}
          </span>
        ),
      },
    ],
    [t]
  );

  const getAssociatedSessions = (record: PatientRecord): Appointment[] =>
    appointments.filter(appointment => appointment.contractRecordId === record.id);

  return (
    <Card
      variant="primary"
      title={t('PatientRecords.history.heading')}
      icon={Files}
      className="w-full p-0 am-md:p-0"
    >
      <ExpandableRecordsTable
        data={records}
        columns={columns}
        getRowId={row => String(row.id)}
        initialSorting={[{ id: 'code', desc: true }]}
        isLoading={isLoading}
        emptyMessage={
          isLoading
            ? t('Common.loading', { ns: LOCIZE_NS.COMMON })
            : t('PatientRecords.history.empty')
        }
        expandAriaLabel={t('PatientRecords.history.expand')}
        collapseAriaLabel={t('PatientRecords.history.collapse')}
        renderExpandedContent={record => (
          <AssociatedSessions sessions={getAssociatedSessions(record)} reports={reports} />
        )}
      />
    </Card>
  );
};
